import { CoreButton, CorePaper } from "../../core";
import styles from "./EditQuizModalComponents.module.css";
import i18nPackage from "../../i18n/i18n";
import { CoreModalDescription, CoreModalProps } from "../../core/modal/Modal";
import { useMobileLayout } from "../../hooks";
import { ConditionalWrapper, TooltipWrapper } from "../../wrapper";
import React, { ComponentPropsWithoutRef } from "react";
import CoreTitleCard, { CoreTitleCardProps } from "../../core/card/TitleCard";
import { CorePaperProps } from "../../core/paper";
import { Skeleton } from "@mui/material";
import { CoreButtonProps } from "../../core/button/Button";

type BaseButtonProps = {
	disabled: boolean;
	onClick: () => void | Promise<void>;
	hide?: boolean;
	text?: string;
	tooltip?: React.ReactNode;
} & Omit<CoreButtonProps, "onClick" | "disabled" | "hide" | "children">;

export type EditQuizFooterProps = {
	slotProps: {
		back?: BaseButtonProps;
		saveProfile?: BaseButtonProps;
		confirm?: BaseButtonProps;
		next: BaseButtonProps;
	};
	className?: string;
};

export function EditQuizFooter({ slotProps, className }: EditQuizFooterProps) {
	const { back, confirm, next, saveProfile } = slotProps;

	return (
		<div className={`${styles.editQuizFooter} ${className || ""}`}>
			{back && (
				<TooltipWrapper condition={Boolean(back.tooltip)} title={back.tooltip}>
					<CoreButton {...back} color="primary">
						{back.text ?? i18nPackage.t("editQuizPage.components.footer.back")}
					</CoreButton>
				</TooltipWrapper>
			)}
			<div className={styles.editQuizFooterRightWrapper}>
				{saveProfile && !saveProfile.hide && (
					// using tooltip wrapper so that the spans and stuff gets applied and maybe in the future want to conditionally render tooltip
					<TooltipWrapper
						condition={true}
						wrapInSpan={false}
						title={saveProfile.tooltip ?? i18nPackage.t("editQuizPage.components.footer.saveAsProfileTooltip")}
					>
						<CoreButton {...saveProfile} color="primary" aria-haspopup="dialog">
							{saveProfile.text ?? i18nPackage.t("editQuizPage.components.footer.saveAsProfile")}
						</CoreButton>
					</TooltipWrapper>
				)}
				{!next.hide && (
					<TooltipWrapper condition={Boolean(next.tooltip)} title={next.tooltip}>
						<CoreButton color="primary" {...next}>
							{next.text ?? i18nPackage.t("editQuizPage.components.footer.next")}
						</CoreButton>
					</TooltipWrapper>
				)}
				{confirm && !confirm.hide && (
					<TooltipWrapper condition={Boolean(confirm.tooltip)} title={confirm.tooltip}>
						<CoreButton {...confirm} color="primary">
							{confirm.text ?? i18nPackage.t("editQuizPage.components.footer.confirm")}
						</CoreButton>
					</TooltipWrapper>
				)}
			</div>
		</div>
	);
}

export function EditQuizModalSkeleton() {
	return (
		<div className={styles.editQuizSkeletonWrapper}>
			<Skeleton variant="rounded" height={"100%"} width={"25%"} />
			<Skeleton variant="rounded" height={"100%"} width={"75%"} />
		</div>
	);
}

const MODAL_WIDTH_MOBILE: Partial<CoreModalProps> = {
	minWidth: undefined,
	minHeight: undefined,
	widthFixed: true,
};

const MODAL_WIDTH: Partial<CoreModalProps> = {
	minWidth: "min(90%, 980px)",
	minHeight: "min(98%, 710px)",
	widthFixed: true,
};

export function useEditQuizStyles() {
	const getEditQuizModalProps = (isMobileLayout: boolean): Partial<CoreModalProps> => {
		return {
			title: undefined,
			padding: isMobileLayout ? undefined : "1.25rem 0 0 1.25rem",
			dialogContentBackground: isMobileLayout ? "#f4f4f4" : undefined,
			headerSx: {
				borderImage: "none !important",
				borderBottom: "0 !important",
				paddingBottom: "10px",
			},
			fullScreen: isMobileLayout,
			heightFixed: true,
			hideHorizontalOverflow: true,
			hideVerticalOverflow: true,
			contentMatchModalHeight: true,
			ariaLabel: i18nPackage.t("editQuizPage.setupProctoring"),
			...(isMobileLayout ? MODAL_WIDTH_MOBILE : MODAL_WIDTH),
		};
	};

	const getEditQuizModalWidth = (isMobileLayout: boolean): Partial<CoreModalProps> => {
		return isMobileLayout ? MODAL_WIDTH_MOBILE : MODAL_WIDTH;
	};

	/**styling for modal in modals */
	const getModalInModalProps = (isMobileLayout: boolean, title?: React.ReactNode): Partial<CoreModalProps> => {
		return {
			title: isMobileLayout ? undefined : title,
			heightFixed: isMobileLayout,
			fullScreen: isMobileLayout,
			showTopCloseButton: !isMobileLayout,
			dialogContentBackground: isMobileLayout ? "#f4f4f4" : undefined,
		};
	};

	return { getEditQuizModalProps, getEditQuizModalWidth, getModalInModalProps };
}

type ResponsiveModalHeaderProps = {
	isMobileLayout: boolean;
	titleCardProps?: Omit<CoreTitleCardProps, "title" | "description">;
	/**Will only show on mobile */
	title?: React.ReactNode;
	description?: React.ReactNode;
};

function ResponsiveModalHeader({ isMobileLayout, description, titleCardProps, title }: ResponsiveModalHeaderProps) {
	return isMobileLayout ? (
		<CoreTitleCard
			{...titleCardProps}
			className={`${styles.modalTitle} ${titleCardProps?.className || ""}`}
			title={title}
			description={description}
			slotProps={{
				...titleCardProps?.slotProps,
				title: {
					sx: { color: "#42296E", ...titleCardProps?.slotProps?.title?.sx },
					...titleCardProps?.slotProps?.title,
				},
				text: { style: { gap: "5px", ...titleCardProps?.slotProps?.text?.style }, ...titleCardProps?.slotProps?.text },
			}}
		/>
	) : (
		<CoreModalDescription>{description}</CoreModalDescription>
	);
}

type ResponsiveModalContentProps = {
	isMobileLayout: boolean;
	paperProps?: Omit<CorePaperProps, "children">;
	rootProps?: Omit<ComponentPropsWithoutRef<"div">, "children">;
	/**Goes on CorePaper on mobile and wrapper <div> when not mobile. This wraps the children */
	contentClassName?: string;
	children: React.ReactNode;
};

function ResponsiveModalContent({
	isMobileLayout,
	rootProps,
	contentClassName,
	paperProps,
	children,
}: ResponsiveModalContentProps) {
	return (
		<ConditionalWrapper
			condition={isMobileLayout}
			whenTrue={(children) => (
				<CorePaper
					{...paperProps}
					sx={{ padding: "0.5rem", ...paperProps?.sx }}
					component="section"
					className={`${contentClassName || ""} ${paperProps?.className || ""}`}
				>
					{children}
				</CorePaper>
			)}
			whenFalse={(children) => (
				<div {...rootProps} className={`${contentClassName || ""} ${rootProps?.className || ""}`}>
					{children}
				</div>
			)}
		>
			{children}
		</ConditionalWrapper>
	);
}

type ResponsiveModalBodyProps = {
	slotProps?: {
		modalContainer?: Omit<ComponentPropsWithoutRef<"div">, "children">;
		titleCard?: ResponsiveModalHeaderProps["titleCardProps"];
		paper?: ResponsiveModalContentProps["paperProps"];
		contentRoot?: ResponsiveModalContentProps["rootProps"];
	};
	children: React.ReactNode;
} & Pick<ResponsiveModalHeaderProps, "description" | "title"> &
	Pick<ResponsiveModalContentProps, "contentClassName">;

/**Handles mobile layout for modals in modals */
export function ResponsiveModalBody({
	slotProps,
	description,
	title,
	children,
	contentClassName,
}: ResponsiveModalBodyProps) {
	const isMobileLayout = useMobileLayout();

	return (
		<div
			{...slotProps?.modalContainer}
			className={`${styles.modalContainer} ${slotProps?.modalContainer?.className || ""}`}
		>
			<ResponsiveModalHeader
				{...slotProps?.titleCard}
				isMobileLayout={isMobileLayout}
				title={title}
				description={description}
			/>

			<ResponsiveModalContent
				isMobileLayout={isMobileLayout}
				contentClassName={contentClassName}
				rootProps={slotProps?.contentRoot}
				paperProps={slotProps?.paper}
			>
				{children}
			</ResponsiveModalContent>
		</div>
	);
}
